import { z } from 'zod';
import { Id, IsoDate } from './common';
import { WorkflowStatus } from './scans';

/**
 * Jobs asynchrones (`data-model.md` § `jobs`, `api.md` § Conventions › Idempotence). Les routes
 * `202` renvoient `JobAccepted` ; `GET /jobs/:id` renvoie `JobRow`. Le consumer de queue
 * (`pipeline.md` § Consumer `radar-tasks`) fait évoluer `status` à partir de `JobMessage`.
 */

export const JOB_TYPES = [
  'refresh_serp',
  'reanalyze_cluster',
  'rescore_all',
  'send_email',
  'export_brief',
] as const;
export const JobType = z.enum(JOB_TYPES);
export type JobType = z.infer<typeof JobType>;

export const JOB_STATUSES = ['queued', 'running', 'done', 'failed'] as const;
export const JobStatus = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatus>;

/** Statuts pendant lesquels l'UI continue d'interroger `GET /jobs/:id`. */
export const JOB_ACTIVE_STATUSES: readonly JobStatus[] = ['queued', 'running'];

export function isJobActive(status: JobStatus): boolean {
  return JOB_ACTIVE_STATUSES.includes(status);
}

/**
 * Message de la queue `radar-tasks`. Source de vérité unique du couple `(type, payload)` :
 * `apps/server/src/queue` le valide à la réception (les corps de message sont `unknown`).
 */
export const JobMessage = z.object({
  type: JobType,
  jobId: Id,
  payload: z.record(z.string(), z.unknown()).default({}),
});
export type JobMessage = z.infer<typeof JobMessage>;

export const JobRow = z.object({
  id: Id,
  type: JobType,
  status: JobStatus,
  attempts: z.number().int(),
  result: z.record(z.string(), z.unknown()).nullable(),
  error: z.string().nullable(),
  createdAt: IsoDate,
  startedAt: IsoDate.nullable(),
  finishedAt: IsoDate.nullable(),
  /** Instance `RefreshSerpWorkflow` d'un job `refresh_serp` ; `null` sinon ou si introuvable. */
  workflowStatus: WorkflowStatus.nullable().optional(),
});
export type JobRow = z.infer<typeof JobRow>;

/** `202 { jobId, existing }` : `existing` quand un job identique était déjà `queued | running`. */
export const JobAccepted = z.object({ jobId: Id, existing: z.boolean() });
export type JobAccepted = z.infer<typeof JobAccepted>;

/** `POST /opportunities/:id/refresh-serp` : `reanalyze` déclenche l'analyse profonde après la SERP. */
export const RefreshSerpBody = z.object({ reanalyze: z.boolean().default(false) });
export type RefreshSerpBody = z.infer<typeof RefreshSerpBody>;
